import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Minus, Plus, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';

const dateParts = (d) => {
  const dt = new Date(d + 'T00:00:00');
  return {
    weekday: dt.toLocaleDateString('en-US', { weekday: 'short' }),
    day: dt.toLocaleDateString('en-US', { day: 'numeric' }),
    month: dt.toLocaleDateString('en-US', { month: 'short' }),
  };
};

const minutesBetween = (a, b) => {
  const [ah, am] = a.split(':').map(Number);
  const [bh, bm] = b.split(':').map(Number);
  return bh * 60 + bm - (ah * 60 + am);
};

const BookingTimeline = ({ teacherId, onSelectionChange }) => {
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [cells, setCells] = useState([]);
  const [loadingDates, setLoadingDates] = useState(true);
  const [loadingCells, setLoadingCells] = useState(false);
  const [sel, setSel] = useState(null); // {lo, hi, anchor}

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/teachers/${teacherId}/available-dates`);
        setDates(data || []);
      } catch (e) {
        // silent
      } finally {
        setLoadingDates(false);
      }
    })();
  }, [teacherId]);

  const loadTimeline = useCallback(async (date) => {
    setSelectedDate(date);
    setSel(null);
    onSelectionChange?.(null);
    setLoadingCells(true);
    try {
      const { data } = await axios.get(`${API}/teachers/${teacherId}/timeline`, { params: { date } });
      setCells(data.cells || []);
    } catch (e) {
      toast.error('Could not load slots for this date');
      setCells([]);
    } finally {
      setLoadingCells(false);
    }
  }, [teacherId, onSelectionChange]);

  const rangeAvailable = (lo, hi) => cells.slice(lo, hi + 1).every((c) => c.status === 'available');

  const emit = (next) => {
    setSel(next);
    if (!next) { onSelectionChange?.(null); return; }
    const start = cells[next.lo].start_time;
    const end = cells[next.hi].end_time;
    onSelectionChange?.({
      date: selectedDate,
      start_time: start,
      end_time: end,
      duration_hours: minutesBetween(start, end) / 60,
    });
  };

  const handleCellClick = (i) => {
    if (cells[i].status !== 'available') return;
    if (!sel) { emit({ lo: i, hi: i, anchor: i }); return; }
    if (i === sel.anchor) { emit(null); return; } // click the anchor again to deselect
    const lo = Math.min(sel.anchor, i);
    const hi = Math.max(sel.anchor, i);
    if (rangeAvailable(lo, hi)) emit({ lo, hi, anchor: sel.anchor });
    else emit({ lo: i, hi: i, anchor: i });
  };

  const extend = () => {
    if (!sel) return;
    const next = sel.hi + 1;
    if (next < cells.length && cells[next].status === 'available') emit({ ...sel, hi: next });
    else toast.info('No more continuous availability to extend into');
  };

  const shrink = () => {
    if (!sel || sel.hi === sel.lo) return;
    emit({ ...sel, hi: sel.hi - 1 });
  };

  const cellClass = (c, i) => {
    const selected = sel && i >= sel.lo && i <= sel.hi;
    if (selected) return 'bg-violet-600 text-white border-violet-600 shadow-md shadow-violet-500/30';
    if (c.status === 'available') return 'bg-white text-slate-700 border-violet-200 hover:border-violet-400 hover:bg-violet-50 cursor-pointer';
    if (c.status === 'booked') return 'bg-rose-50 text-rose-300 border-rose-100 cursor-not-allowed line-through';
    return 'bg-slate-100 text-slate-300 border-slate-100 cursor-not-allowed';
  };

  if (loadingDates) {
    return <div className="py-8 text-center text-slate-500 text-sm">Loading availability...</div>;
  }

  if (dates.length === 0) {
    return (
      <div className="py-8 text-center" data-testid="no-available-dates">
        <CalendarDays className="w-8 h-8 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500">No slots available. Please check back later.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="booking-timeline">
      {/* Date strip */}
      <div>
        <p className="text-sm font-medium text-slate-700 mb-2">Select a date</p>
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="date-strip">
          {dates.map((d) => {
            const p = dateParts(d.date);
            const active = selectedDate === d.date;
            const soldOut = d.available_slots === 0;
            return (
              <button
                key={d.date}
                onClick={() => !soldOut && loadTimeline(d.date)}
                disabled={soldOut}
                data-testid={`date-option-${d.date}`}
                className={`flex-shrink-0 w-20 rounded-xl border px-2 py-2 text-center transition-all ${
                  active
                    ? 'border-violet-600 bg-violet-600 text-white shadow-md shadow-violet-500/30'
                    : soldOut
                    ? 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-violet-400'
                }`}
              >
                <div className="text-xs font-medium">{p.weekday}</div>
                <div className="text-lg font-bold leading-tight">{p.day}</div>
                <div className="text-xs">{p.month}</div>
                <div className={`mt-1 text-[10px] font-medium ${active ? 'text-violet-100' : soldOut ? 'text-slate-300' : 'text-emerald-600'}`}>
                  {soldOut ? 'Full' : `${d.available_slots} slots`}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Timeline */}
      {selectedDate && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-slate-700">Pick your time</p>
            <div className="flex items-center gap-3 text-[11px] text-slate-500">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-violet-200 bg-white inline-block" /> Available</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-violet-600 inline-block" /> Selected</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-50 border border-rose-100 inline-block" /> Booked</span>
            </div>
          </div>

          {loadingCells ? (
            <div className="py-8 text-center text-slate-500 text-sm">Loading slots...</div>
          ) : cells.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500" data-testid="no-slots-for-date">No slots available for this date.</div>
          ) : (
            <>
              <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1" data-testid="timeline-cells">
                {cells.map((c, i) => (
                  <button
                    key={c.start_time}
                    onClick={() => handleCellClick(i)}
                    disabled={c.status !== 'available' && !(sel && i >= sel.lo && i <= sel.hi)}
                    data-testid={`slot-${c.start_time}`}
                    data-status={c.status}
                    className={`flex-shrink-0 w-16 rounded-lg border px-1 py-2 text-center text-xs font-medium transition-all ${cellClass(c, i)}`}
                    title={`${c.start_time} - ${c.end_time} (${c.status})`}
                  >
                    {c.start_time}
                  </button>
                ))}
              </div>

              {/* Selection summary */}
              {sel ? (
                <div className="mt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-violet-50 border border-violet-200 rounded-xl p-3" data-testid="selection-summary">
                  <div className="text-sm">
                    <span className="text-slate-500">Selected: </span>
                    <span className="font-semibold text-slate-900" data-testid="selection-start">{cells[sel.lo].start_time}</span>
                    <span className="text-slate-400"> – </span>
                    <span className="font-semibold text-slate-900" data-testid="selection-end">{cells[sel.hi].end_time}</span>
                    <span className="ml-2 text-violet-700 font-medium" data-testid="selection-duration">
                      ({(sel.hi - sel.lo + 1) * 30} mins)
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={shrink} disabled={sel.hi === sel.lo} className="rounded-full h-8 w-8 p-0" data-testid="shrink-slot-button">
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="text-xs text-slate-500 w-14 text-center">30 min</span>
                    <Button type="button" variant="outline" size="sm" onClick={extend} className="rounded-full h-8 w-8 p-0" data-testid="extend-slot-button">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="mt-3 text-xs text-slate-500">Tap a start time, then tap a later time (or use +) to extend in 30-minute steps. Tap the selected start again to clear. Minimum 30 minutes.</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingTimeline;
